import {useApolloClient, useFragment} from '@apollo/client'
import {useCallback} from 'react'

import {
  EufePageFragmentDoc,
  PanelFragmentDoc,
  XcDisclaimerInterfaceFragmentDoc,
  XcNwBlockFragmentDoc,
  XcNwBlockLockStatusFragmentDoc,
  XcNwDataFragmentDoc,
} from '../generated/graphql'
import generatedGraphql from '../generated/possibleTypes'
import {getGraphQlFragmentVariables} from '../client'

import type {EufePageFragment} from '../generated/graphql'
import type {
  LockStatusFragment,
  PanelFragment,
  XcDisclaimerInterfaceFragment,
  XcNwBlockFragment,
  XcNwBlockLockStatusFragment,
  XcNwDataFragment,
} from '../generated/graphql'

export const blockTypes = generatedGraphql.possibleTypes['BlockInterface']
export const recordTypes = generatedGraphql.possibleTypes['XCFolderDocumentInterface']

export const usePageFragment = (id: EufePageFragment['id']): {complete: boolean; data: EufePageFragment} => {
  const {complete, data} = useFragment({
    fragment: EufePageFragmentDoc,
    fragmentName: 'EufePage',
    from: {
      __typename: 'EufePageType',
      id: id,
    },
    variables: getGraphQlFragmentVariables(),
  })
  return {complete, data}
}

type UseDisclaimerFragment = (props: Partial<Pick<XcDisclaimerInterfaceFragment, '__typename' | 'id'>>) => {
  complete: boolean
  data: XcDisclaimerInterfaceFragment
}

export const useDisclaimerFragment: UseDisclaimerFragment = ({id, __typename = 'XCInfoFootnoteType'}) => {
  const {complete, data} = useFragment({
    fragment: XcDisclaimerInterfaceFragmentDoc,
    from: {
      id,
      __typename,
    },
    fragmentName: 'XCDisclaimerInterface',
    variables: getGraphQlFragmentVariables(),
  })

  return {complete, data}
}

export const useFragmentHelpers = () => {
  const client = useApolloClient()

  const getDocument = (documentId: string): XcNwDataFragment => {
    const result = client.readFragment<XcNwDataFragment>({
      fragment: XcNwDataFragmentDoc,
      fragmentName: 'XCNwData',
      id: `DocumentType:${documentId}`,
      variables: getGraphQlFragmentVariables(),
    })
    if (!result) throw new Error('Programming error: unable to read document fragment')
    return result
  }

  const updateDocument = (documentId: string, documentData: XcNwDataFragment) => {
    client.writeFragment<XcNwDataFragment>({
      id: `DocumentType:${documentId}`,
      fragment: XcNwDataFragmentDoc,
      fragmentName: 'XCNwData',
      data: documentData,
      variables: getGraphQlFragmentVariables(),
    })
  }

  const getPanel = (panelId: string): PanelFragment => {
    const fragmentId = `PanelType:${panelId}`
    const panel = client.readFragment<PanelFragment>({
      id:fragmentId,
      fragment: PanelFragmentDoc,
      fragmentName: 'Panel',
      variables: getGraphQlFragmentVariables(),
    })
    if (!panel) throw new Error(`Programming error - trying to access a not existing cache object: ${fragmentId}`)
    return panel
  }

  const getPanelFromBlockId = (blockId: string) => {
    const cacheData = client.cache.extract()
    const panelRefs = Object.values(cacheData).filter((data: any): data is {id: string; __typename: 'PanelType'} => data.__typename === 'PanelType')
    for (const panelRef of panelRefs){
      const panel = getPanel(panelRef.id)
      for (const block of panel.blocks){
        if (block.id === blockId) return panel

      }
    }
  }

  const usePanel = (panelId: string, panelData?: PanelFragment) => {
    const {data, complete} =  useFragment<PanelFragment>({
      fragment: PanelFragmentDoc,
      fragmentName: 'Panel',
      from: {
        __typename: 'PanelType',
        id: panelId,
      },
      variables: getGraphQlFragmentVariables(),
    })
    if ((!complete || !data) && panelData) return panelData
    if (!complete) throw new Error(`Programming error - trying to access an incomplete cache object: PanelType:${panelId}`)
    if (!data) throw new Error(`Programming error - trying to access a not existing cache object: PanelType:${panelId}`)
    return data
  }

  const updatePanel = (panelId: string, panelData: PanelFragment) => {
    client.writeFragment<PanelFragment>({
      id: `PanelType:${panelId}`,
      fragment: PanelFragmentDoc,
      fragmentName: 'Panel',
      data: panelData,
      variables: getGraphQlFragmentVariables(),
    })
  }

  const deletePanel = (panel: PanelFragment) => client.cache.evict({id: `PanelType:${panel.id}`})

  const getBlock = useCallback((blockId: string): XcNwBlockFragment => {
    for (const possibleType of blockTypes) {
      const block = client.readFragment<XcNwBlockFragment>({
        id: `${possibleType}:${blockId}`,
        fragment: XcNwBlockFragmentDoc,
        fragmentName: 'XCNwBlock',
        variables: getGraphQlFragmentVariables(),
      })
      if (block) return block
    }
    throw new Error(`Programming error - trying to find a not existing block: ${blockId}`)
  }, [client ])

  const updateBlockLockStatus = useCallback(
    (blockId: string, lockStatus: LockStatusFragment | null)  => {
      const block = getBlock(blockId)
      client.writeFragment<XcNwBlockLockStatusFragment>({
        id: `${block.__typename}:${block.id}`,
        fragment: XcNwBlockLockStatusFragmentDoc,
        fragmentName: 'XCNwBlockLockStatus',
        data: {
          ...block,
          lockStatus,
        },
        variables: getGraphQlFragmentVariables(),
      })
    }, [client, getBlock])

  const useBlockLockStatus = (blockId: string, __typename: string): LockStatusFragment | null => {
    const {complete, data} = useFragment<XcNwBlockLockStatusFragment>({
      fragment: XcNwBlockLockStatusFragmentDoc,
      fragmentName: 'XCNwBlockLockStatus',
      from: {
        id: blockId,
        __typename,
      },
      variables: getGraphQlFragmentVariables(),
    })

    if (!complete) return null
    if (!data) throw new Error(`Programming error - trying to access a not existing cache object: ${__typename}:${blockId}`)

    return data.lockStatus
  }

  const usePageLockStatus = (pageId: string): LockStatusFragment | null => {
    const {complete, data} = useFragment<XcNwBlockLockStatusFragment>({
      fragment: XcNwBlockLockStatusFragmentDoc,
      fragmentName: 'XCNwBlockLockStatus',
      from: {
        id: pageId,
        __typename: 'EufePageType',
      },
      variables: getGraphQlFragmentVariables(),
    })
    if (!complete) return null
    if (!data) throw new Error(`Programming error - trying to access a not existing cache object: EufePageType:${pageId}`)

    return data.lockStatus
  }

  const evictFolderRecord = (recordId: string) => {
    for (const possibleType of recordTypes) {
      // record id is always unique, so this operation is safe, even it is iterating over all possible types
      client.cache.evict({id: `${possibleType}:${recordId}`})
    }
  }

  return {
    getDocument,
    updateDocument,
    getBlock,
    getPanel,
    updatePanel,
    deletePanel,
    usePanel,
    useBlockLockStatus,
    usePageLockStatus,
    evictFolderRecord,
    updateBlockLockStatus,
    getPanelFromBlockId,
  }
}
